// Matches the order service's CHECK constraint. 'dispensing' is RETIRED — it is
// no longer offered in ORDER_STATUSES, so nothing can move into it, but the
// member stays so orders already sitting in that state still type-check and
// render (badge + chart colour are likewise kept).
export type OrderStatus =
  | 'pending'
  | 'confirmed'
  | 'processing'
  | 'formulated'
  | 'dispensing'
  | 'ready_to_ship'
  | 'shipped'
  | 'delivered'
  | 'completed'
  | 'cancelled'
  | 'refunded';

export type PaymentStatus = 'pending' | 'paid' | 'failed' | 'refunded' | 'partially_refunded';

// Shiprocket courier lifecycle, tracked independently of the order status.
export type ShippingStatus =
  | 'ready_to_ship'
  | 'shipped'
  | 'delivered'
  | 'rto_initiated'
  | 'rto_delivered';

export interface Order {
  id: string;
  user_id: string;
  order_type: 'formulation' | 'ecommerce';
  subtotal: number;
  tax_amount: number;
  discount_amount: number;
  total: number;
  status: OrderStatus;
  payment_status: PaymentStatus;
  payment_method: string;
  coupon_code: string | null;
  awb_number: string | null;
  courier_name: string | null;
  tracking_url: string | null;
  estimated_delivery: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
  // Shiprocket integration (admin-triggered fulfilment)
  shiprocket_order_id: string | null;
  shiprocket_shipment_id: string | null;
  courier_id: number | null;
  label_url: string | null;
  shipping_status: ShippingStatus | null;
  pickup_scheduled_at: string | null;
  // 'self_ship' = delivered by hand, no Shiprocket shipment. Absent on old responses.
  fulfillment_type?: 'shiprocket' | 'self_ship' | null;
  created_at: string;
  updated_at: string;
  customer_name?: string | null; // flattened onto list rows by the admin list endpoint
  has_custom?: number | boolean; // list rows: order has a modern custom (formulated) item
  items: OrderItem[];
  shipping_address: Address | null;
  payment: Payment | null;
  formulation_details: FormulationDetails | null;
  custom_formulas?: CustomFormula[]; // modern per-custom-item oil formulas (detail only)
  status_history: StatusHistory[];
  shipments?: OrderShipment[];       // Shiprocket shipments (detail only)
  remaining_items?: RemainingItem[]; // per-item units still to ship (detail only)
}

// A modern custom line item's oil formula, resolved from the AI service (or an
// admin override). One per custom item on an ecommerce order.
export interface CustomFormula {
  order_item_id: string;
  product_name: string;
  formulation_id: string | null;
  oils: FormulaOil[];
  overridden: boolean;
  edited_by: string | null;
  edited_at: string | null;
}

export interface OrderItem {
  id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  metadata: Record<string, any>;
}

export interface Address {
  full_name: string;
  phone: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  pincode: string;
}

export interface Payment {
  method: string;
  amount: number;
  cashfree_order_id: string | null;
  status: PaymentStatus;
}

export type OilTier = 'top' | 'heart' | 'base' | '';

export interface FormulaOil {
  name: string;
  ml: number;
  tier?: OilTier;
}

export interface FormulationEdit {
  changed_by: string;
  changed_at: string;
  summary: string;
}

export interface FormulationDetails {
  formulation_id: string;
  bottle_size_ml: number;
  personality_summary: string;
  personality_data: Record<string, any>;
  // The oil formula (per-oil ml breakdown) admins compound + can edit. `oils` is
  // a normalized flat list; `formula_style` records how the service stores it.
  oils?: FormulaOil[];
  formula_style?: 'flat' | 'tiered';
  // Admin-only audit trail of formula edits, newest first.
  edit_history?: FormulationEdit[];
}

export interface StatusHistory {
  status: string;
  changed_at: string;
  changed_by: string;
  notes: string;
}

export interface ShippingInfo {
  awb_number: string;
  courier_name: string;
  tracking_url: string;
  estimated_delivery: string;
}

// A courier option returned by the Shiprocket serviceability check.
export interface CourierOption {
  id: number;
  name: string;
  rate: number;
  estimated_delivery_days: number;
  estimated_delivery_date: string;
}

/**
 * How far a shipment got through Shiprocket's createOrder → assignCourier →
 * generateLabel sequence. Drives what the Shipping panel offers: a stalled
 * shipment shows where it stopped and a Retry that resumes from exactly there.
 *   order_created    — Shiprocket order exists, no courier/AWB yet
 *   courier_assigned — AWB assigned (and billed), label still missing
 *   label_generated  — label came back; rolls up to complete
 *   complete         — AWB + label, nothing outstanding
 *   failed           — a step errored; shipment_error says which and why
 */
export type ShipmentStep =
  | 'order_created'
  | 'courier_assigned'
  | 'label_generated'
  | 'complete'
  | 'failed';

// Result of creating a Shiprocket shipment.
export interface ShipmentResult {
  shipment_id: string;
  seq: number;
  awb_number: string;
  courier_name: string | null;
  label_url: string | null;
  shipment_step?: ShipmentStep;
  resumed?: boolean;
  success: boolean;
}

// What a "Sync from Shiprocket" pulled back. `changes_made` lists the columns
// that actually differed, so the admin sees at a glance what the sync found —
// an empty array means Shiprocket shows exactly what we already had.
export interface SyncShipmentResult {
  synced: boolean;
  awb_number: string | null;
  courier_name: string | null;
  label_url: string | null;
  shipment_step: ShipmentStep | null;
  changes_made: string[];
  shipments: {
    shipment_id: string;
    seq: number;
    shiprocket_order_id: string | null;
    awb_number: string | null;
    courier_name: string | null;
    label_url: string | null;
    shipment_step: ShipmentStep;
    shiprocket_status: string | null;
    changes_made: string[];
  }[];
  success: boolean;
}

// One item allocation in a (partial) shipment.
export interface ShipmentItem {
  order_item_id: string;
  product_name: string;
  units: number;
}

// A Shiprocket shipment for an order (an order may have several).
export interface OrderShipment {
  id: string;
  seq: number;
  shiprocket_order_id: string | null;
  shiprocket_shipment_id: string | null;
  awb_number: string | null;
  courier_id: number | null;
  courier_name: string | null;
  label_url: string | null;
  shipping_status: ShippingStatus | null;
  pickup_scheduled_at: string | null;
  items: ShipmentItem[];
  // Where this shipment stopped, and why. The backend derives a step for rows
  // created before it tracked one, so this is null only for a shipment that
  // never reached Shiprocket at all.
  shipment_step: ShipmentStep | null;
  shipment_error: string | null;
  created_at: string;
}

// Per-item remaining units to ship (ordered − already shipped across shipments).
export interface RemainingItem {
  order_item_id: string;
  product_id: string | null;
  product_name: string;
  unit_price: number;
  ordered: number;
  shipped_units: number;
  remaining: number;
}

/**
 * Ghost-order semantics: online (non-COD) orders start as 'pending' and only
 * become 'confirmed' after payment succeeds. A pending unpaid online order
 * means the customer is still at the payment gateway — it is NOT an actionable
 * new order, and the system auto-cancels it if payment never completes.
 */
export function isAwaitingPayment(
  o: Pick<Order, 'status' | 'payment_method' | 'payment_status'>
): boolean {
  return o.status === 'pending' && o.payment_method !== 'cod' && o.payment_status === 'pending';
}

// Selectable statuses — drives both the status dropdown and the orders filter.
// 'dispensing' is deliberately absent (retired); 'processing' covers that stage.
export const ORDER_STATUSES: OrderStatus[] = [
  'pending',
  'confirmed',
  'processing',
  'formulated',
  'ready_to_ship',
  'shipped',
  'delivered',
  'completed',
  'cancelled',
  'refunded',
];

// Order statuses driven automatically by the Shiprocket courier lifecycle — the
// admin shouldn't set these by hand (create a shipment / schedule pickup instead).
export const COURIER_DRIVEN_STATUSES: OrderStatus[] = ['shipped', 'delivered'];

// The forward lifecycle, in order, for "has this order reached stage X?" checks.
// 'cancelled' and 'refunded' are deliberately absent: they are terminal branches
// that can be entered from ANY stage, so an order cancelled while still pending
// must not count as having reached 'processing'. Retired 'dispensing' is kept in
// sequence so orders still parked there compare correctly.
const LIFECYCLE_ORDER: OrderStatus[] = [
  'pending',
  'confirmed',
  'processing',
  'formulated',
  'dispensing',
  'ready_to_ship',
  'shipped',
  'delivered',
  'completed',
];

/**
 * Has the order reached `stage` or moved past it? Returns false for the terminal
 * cancelled/refunded branches, which sit outside the forward lifecycle.
 */
export function hasReachedStage(status: OrderStatus, stage: OrderStatus): boolean {
  const at = LIFECYCLE_ORDER.indexOf(status);
  const target = LIFECYCLE_ORDER.indexOf(stage);
  return at !== -1 && target !== -1 && at >= target;
}
